import { fetchWithAuth } from '../helpers';

// ==================== FOLDERS ====================

const getAllFolders = async () => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/folders`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

const createFolder = async (data: {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  visibility?: 'public' | 'private' | 'class';
}) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/folders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

const updateFolder = async (id: number, data: {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  visibility?: 'public' | 'private' | 'class';
}) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/folders/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

const deleteFolder = async (id: number) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/folders/${id}`, {
    method: 'DELETE',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

// ==================== SETS ====================

const getAllSets = async (folderId?: number) => {
  const url = folderId 
    ? `${process.env.REACT_APP_API_URL}/api/flashcards/sets?folderId=${folderId}`
    : `${process.env.REACT_APP_API_URL}/api/flashcards/sets`;
  
  const response = await fetchWithAuth(url, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

const getSetById = async (id: number) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${id}`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

const createSet = async (data: {
  folder_id?: number;
  name: string;
  description?: string;
  language_from?: string;
  language_to?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  tags?: string[];
  visibility?: 'public' | 'private' | 'class';
}) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

const updateSet = async (id: number, data: {
  folder_id?: number;
  name?: string;
  description?: string;
  language_from?: string;
  language_to?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  tags?: string[];
  visibility?: 'public' | 'private' | 'class';
}) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

const deleteSet = async (id: number) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${id}`, {
    method: 'DELETE',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

// ==================== CARDS ====================

const getAllCards = async (setId: number) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${setId}/cards`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

const getCardsBySet = async (setId: number) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${setId}/cards`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

const createCard = async (setId: number, data: {
  front: string;
  back: string;
  phonetic?: string;
  example?: string;
  note?: string;
  image_url?: string;
  audio_url?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${setId}/cards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

const bulkCreateCards = async (setId: number, cards: Array<{
  front: string;
  back: string;
  phonetic?: string;
  example?: string;
  note?: string;
  image_url?: string;
  audio_url?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}>) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${setId}/cards/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify({ cards }),
  });
  return handleResponse(response);
};

const updateCard = async (id: number, data: {
  front?: string;
  back?: string;
  phonetic?: string;
  example?: string;
  note?: string;
  image_url?: string;
  audio_url?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/cards/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

const deleteCard = async (id: number) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/cards/${id}`, {
    method: 'DELETE',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

const reorderCards = async (setId: number, cardIds: number[]) => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/sets/${setId}/cards/reorder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify({ cardIds }),
  });
  return handleResponse(response);
};

// ==================== DICTIONARY API ====================

const lookupWord = async (word: string, targetLang: string = 'vi') => {
  const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/flashcards/dictionary/${encodeURIComponent(word)}?targetLang=${targetLang}`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': '69420' },
  });
  return handleResponse(response);
};

// ==================== HELPER ====================

function handleResponse(response: Response) {
  return response.text().then((text) => {
    const result = text && JSON.parse(text);
    if (!response.ok) {
      const error = (result && result.message) || response.statusText;
      return Promise.reject(error);
    }
    // Return data field if exists (API wraps response in { success, data }), otherwise return full result
    return result?.data !== undefined ? result.data : result;
  });
}

export const flashcardService = {
  // Folders
  getAllFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  
  // Sets
  getAllSets,
  getSetById,
  createSet,
  updateSet,
  deleteSet,
  
  // Cards
  getAllCards,
  getCardsBySet,
  createCard,
  bulkCreateCards,
  updateCard,
  deleteCard,
  reorderCards,
  
  // Dictionary
  lookupWord,
};
